import * as PIXI from 'pixi.js';
import * as TweenJS from "slotworks/utils/tween";
import Components from 'slotworks/components/components';
import Layers from "slotworks/graphics/layers/layers";
import gameState from 'slotworks/model/game-state';
import SpinRecord from 'slotworks/model/gameplay/records/spin-record';
import { sequence } from 'slotworks/utils/race-utils';
import QuickHitElement from './quickhit-element';
import soundManager from 'slotworks/sound/sound-manager';
import ScatterResult from 'slotworks/model/gameplay/records/results/scatter-result';
import Contract from 'slotworks/utils/contract';

const START = 6;
const END = 15;

export default class QuickHitComponent {

    private layer: Layers;

    private bordersLayer: Layers;

    private elements: QuickHitElement[] = [];

    private harleySymbols: number = 0;
    private highlightedValue: number = -1;
    private spinning: boolean = false;
    private boosters: number = 0;

    private animating: boolean = false;
    private currentValue: number = -1;

    private anticipatingTween: TweenJS.Tween<PIXI.Sprite> = null;


    constructor() {
        this.layer = Layers.Custom.get("MatrixNeon");
        this.bordersLayer = Layers.Custom.get("MatrixNeon");

        this.hideNeonBorders();

        this.layer.onSceneEnter.add(() => this.setUp());

        Components.betBar.onBetUpdated.add(() => this.reset());
        Components.betMenu.onBetOptionsCommitted.add(() => this.reset());

        const onStart = () => {
            this.spinning = true;
            this.harleySymbols = 0;
            this.highlightedValue = -1;
            this.currentValue = -1;
            this.animating = false;

            this.hideNeonBorders();
            this.stopAnticipation();

            this.startSpin();
        }

        const onLand = (index: number) => {
            let winCount = 0;
            if (index === 5) {
                this.spinning = false;
                this.stopSpin();
            }
            const record = gameState.getCurrentGame().getCurrentRecord() as SpinRecord;
            const scatter = record.getResultsOfType(ScatterResult).filter(r => (r as ScatterResult).symbolType === "HARLEY");
            if (scatter.length > 0) {
                winCount = (scatter[0] as ScatterResult).matches;
            }

            const prev = this.harleySymbols;

            const symbols = record.grid[index - 1];
            for (let symbol of symbols) {
                if (symbol === "HARLEY") {
                    this.harleySymbols++;
                }
            }
            let current = this.highlightedValue;
            if (prev != this.harleySymbols) {

                for (let i = START; i <= END; i++) {
                    if (this.harleySymbols + this.boosters >= i) {
                        current = i;
                    } else {
                        break;
                    }
                }
            }

            if (current > this.highlightedValue) {
                this.highlightedValue = current;
                soundManager.customEvent("scatter_land_" + (index - 1));

                this.startAnticipation();
                this.landFlicker();
            } else if (index === 2 && this.harleySymbols >= 5) {
                // no neon border, only landing sound
                soundManager.customEvent("scatter_land_" + 1);
            }
        }

        Components.matrix.startSignal.add(onStart);
        Components.matrix.landSignal.add(onLand);

        this.setUp();

        for (let i = START; i <= END; i++) {
            this.elements[i] = new QuickHitElement(i);
        }

        this.init();
    }

    private init() {
        this.hideNeonBorders();
        this.stopAnticipation();

        const spin = Layers.Custom.get("MatrixAnim").getAnimatedSprite("spin");
        spin.visible = true;
    }

    private landFlicker() {
        if (!this.animating) {
            this.animating = true;
            if (this.currentValue < this.highlightedValue) {
                const funcs = [];
                const flickerTime = this.spinning ? 60 : 20;
                for (let i = Math.max(this.currentValue, START); i <= this.highlightedValue; i++) {
                    funcs.push(() => this.elements[i].flicker(flickerTime));
                }
                this.currentValue = this.highlightedValue;
                sequence(funcs).then(() => {
                    this.animating = false;
                    if (this.currentValue < this.highlightedValue) {
                        this.landFlicker();
                    } else {
                        this.elements[this.highlightedValue].endlessFlicker();
                    }
                });
            }
        }
    }

    public startSpin() {
        const spin = Layers.Custom.get("MatrixAnim").getAnimatedSprite("spin");
        spin.visible = true;
        spin.gotoAndPlay(0);
    }

    public stopSpin() {
        const spin = Layers.Custom.get("MatrixAnim").getAnimatedSprite("spin");
        spin.stop();
        spin.visible = false;
    }

    public restoreDefaultCounts() {
        for (let i = START; i <= END; i++) {
            this.elements[i].resetBoosters();
        }
        this.boosters = 0;
    }

    public restoreFreespinsBoosts(boosts: number) {
        this.boosters = boosts;
        if (this.boosters > 0) {
            for (let i = END; i >= START; i--) {
                this.elements[i].setBoostersInstant(boosts);
            }
        }
    }

    public setFreespinsBoosts(boosts: number) {
        const funcs = [];
        const countTimes = [80, 80, 100, 100, 100, 100, 200, 300, 300, 360];
        this.boosters = boosts;
        if (this.boosters > 0) {
            soundManager.customEvent("quickHit_meter_boost");

            for (let i = END; i >= START; i--) {
                const time = countTimes[i - START];
                funcs.push(() => this.elements[i].setBooster(boosts, time))
            }

            return sequence(funcs);
        } else {
            return Contract.empty();
        }
    }

    public stop() {
        this.hideNeonBorders();
        this.stopAnticipation();
    }

    public refreshBetOptions() {
        this.setUp();
    }

    private reset() {
        setTimeout(() => this.setUp(), 1);
    }

    private setUp() {
        for (let glow of this.elements) {
            glow?.reset();
        }
    }

    private stopelements(elements?: QuickHitElement[]) {
        elements = elements || this.elements;
        for (let glow of elements) {
            glow?.stop();
        }
    }

    private hideNeonBorders() {
        this.stopelements();
        Layers.MatrixBackground.getSprite("Reel_frame_Neon").visible = false;
        this.bordersLayer.getSprite("Quickhit_panel_Neon_Main").visible = false;
    }

    private startAnticipation() {
        if (this.anticipatingTween)
            return;
        const neon = Layers.MatrixBackground.getSprite("Reel_frame_Neon");
        neon.visible = true;
        neon.alpha = 0;
        this.anticipatingTween = new TweenJS.Tween(neon)
            .to({ alpha: 1 }, 200)
            .repeat(Infinity)
            .yoyo(true)
            .start();
    }

    private stopAnticipation() {
        if (this.anticipatingTween) {
            this.anticipatingTween.stop();
            this.anticipatingTween = null;

            const neon = Layers.MatrixBackground.getSprite("Reel_frame_Neon");
            neon.visible = false;
        }
    }

}